import React, { useEffect, useRef } from "react";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import "@splidejs/splide/dist/css/splide.min.css";
import Button from "../../components/ui/Button";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const ReusableSection = ({
  header,
  slides, // Mảng các object chứa link video
  splideOptions: customSplideOptions,
}) => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const sliderRef = useRef(null);

  // Hàm chuyển đổi URL YouTube sang định dạng Embed để chạy được trong iframe
  const getEmbedUrl = (url) => {
    if (!url) return "";
    let videoId = "";

    // Xử lý các định dạng link youtube khác nhau
    if (url.includes("v=")) {
      videoId = url.split("v=")[1].split("&")[0];
    } else if (url.includes("youtu.be/")) {
      videoId = url.split("youtu.be/")[1].split("?")[0];
    } else if (url.includes("embed/")) {
      return url;
    }

    return `https://www.youtube.com/embed/${videoId}`;
  };

  useEffect(() => {
    const context = gsap.context(() => {
      const headerElements = gsap.utils.toArray(
        headerRef.current.querySelectorAll(".section-header-item"),
      );

      headerElements.forEach((element, index) => {
        gsap.set(element, {
          y: 20,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: headerRef.current,
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });

        tl.to(element, {
          y: 0,
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)",
          duration: 1,
          ease: "power2.out",
          delay: index * 0.1,
        });
      });

      if (sliderRef.current) {
        gsap.from(sliderRef.current, {
          opacity: 0,
          y: 50,
          duration: 1.5,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sliderRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        });
      }
    }, sectionRef);

    return () => context.revert();
  }, []);

  const defaultSplideOptions = {
    type: "loop",
    perPage: 1,
    focus: "center",
    autoplay: false,
    interval: 5000,
    flickMaxPages: 1,
    updateOnMove: true,
    pagination: false, // Bật lên để dễ điều hướng giữa các video
    padding: "0%",
    gap: "2rem",
    arrows: true,
    classes: {
      arrows: "splide__arrows your-custom-arrows",
      arrow: "splide__arrow your-custom-arrow",
      prev: "splide__arrow--prev your-custom-prev",
      next: "splide__arrow--next your-custom-next",
    },
    breakpoints: {
      1024: { perPage: 1, padding: "5%" },
      768: { perPage: 1, padding: "0%", gap: "1rem" },
    },
  };

  const finalSplideOptions = {
    ...defaultSplideOptions,
    ...customSplideOptions,
  };

  return (
    <section
      ref={sectionRef}
      className="w-full py-[20px] sm:py-[25px] xl:py-[34px] mt-[50px]"
    >
      <div className="w-full max-w-[1620px] mx-auto px-4 sm:px-6 xl:px-[56px]">
        <div
          ref={headerRef}
          className="text-center mb-[30px] sm:mb-[40px] xl:mb-[50px] space-y-4 xl:space-y-[20px]"
        >
          {/* Title - Uses section-header-item class for GSAP */}
          <h2 className="section-header-item text-[32px] sm:text-[48px] xl:text-xl font-bold leading-[40px] sm:leading-[60px] xl:leading-[100px] text-secondary-text font-[Parkinsans]">
            {header.title}
          </h2>

          {/* Description - Uses section-header-item class for GSAP */}
          <p className="section-header-item text-[20px] xl:text-sm font-light  text-primary-text max-w-[1348px] mx-auto">
            {header.description}
          </p>

          {/* Button - Uses section-header-item class for GSAP */}
          {header.button && (
            <div className="section-header-item">
              <Button
                text={header.button.text}
                onClick={header.button.onClick}
                text_font_size="text-[16px] sm:text-[20px] xl:text-[24px]"
                padding="t=12px,r=32px,b=12px,l=32px"
                text_font_family="Parkinsans"
                text_font_weight="font-semibold"
                text_line_height="leading-normal"
                text_text_align="center"
                // text_color="#1c233a"
                border_border="1px solid #1c233a"
                border_border_radius="rounded-xl"
                layout_width="auto"
                className="hover:bg-secondary-background hover:text-white text-[#1c233a] transition-colors duration-300"
              />
            </div>
          )}
        </div>

        <div ref={sliderRef} className="mb-[40px] sm:mb-[60px] xl:mb-[80px]">
          <Splide options={finalSplideOptions} aria-label="Video Carousel">
            {slides?.map((slide, index) => (
              <SplideSlide key={index}>
                <div className="flex justify-center h-full px-2">
                  <div className="w-full max-w-4xl aspect-video flex items-center justify-center ">
                    <iframe
                      width="600px"
                      height="350px"
                      className="rounded-2xl shadow-lg max-w-full max-h-full lg:max-w-[600px] lg:max-h-[350px] object-cover"
                      src={getEmbedUrl(slide.videoUrl)} // Chuyển đổi link ở đây
                      title={`YouTube video ${index}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              </SplideSlide>
            ))}
          </Splide>
        </div>
      </div>
    </section>
  );
};

export default ReusableSection;
